import { type Ref } from "vue";
import type {
  CompositeFilterDescriptor,
  FilterDescriptor,
} from "@progress/kendo-data-query";

/**
 * Type guard for composite filter groups.
 */
const isCompositeFilter = (
  filterItem: unknown,
): filterItem is CompositeFilterDescriptor => {
  return (
    !!filterItem &&
    typeof filterItem === "object" &&
    "filters" in filterItem &&
    Array.isArray((filterItem as CompositeFilterDescriptor).filters)
  );
};

/**
 * Type guard for leaf filter nodes.
 */
const isFilterDescriptor = (
  filterItem: unknown,
): filterItem is FilterDescriptor => {
  return (
    !!filterItem && typeof filterItem === "object" && "field" in filterItem
  );
};

/**
 * Recursively checks whether a filter tree contains a filter for a specific field.
 */
const hasFieldFilterRecursive = (
  filterItem: unknown,
  field: string,
): boolean => {
  if (!filterItem || typeof filterItem !== "object") {
    return false;
  }

  if (!isCompositeFilter(filterItem)) {
    if (!isFilterDescriptor(filterItem)) {
      return false;
    }
    return filterItem.field === field;
  }

  return filterItem.filters.some((child) =>
    hasFieldFilterRecursive(child, field),
  );
};

/**
 * Returns a reactive function that resolves to `"active"` when the given
 * field has a filter applied in the current filter tree, or `""` when it
 * does not.
 *
 * Bind the returned function directly to column `headerClassName` inside a
 * `computed` — Vue tracks the `filterRef` dependency automatically; no
 * manual update call is needed.
 *
 * @param filterRef - Reactive grid filter descriptor (the same ref bound to the Grid).
 * @returns `(field: string) => "active" | ""`
 *
 * @example
 * const activeClass = useGridActiveFilter(filterRef);
 *
 * const columns = computed(() => [
 *   { field: "ProductName", headerClassName: activeClass("ProductName") },
 * ]);
 */
export const useGridActiveFilter = (
  filterRef: Ref<CompositeFilterDescriptor | undefined>,
): ((field: string) => string) => {
  return (field: string): string => {
    if (!filterRef.value?.filters) return "";
    return filterRef.value.filters.some((f) =>
      hasFieldFilterRecursive(f, field),
    )
      ? "active"
      : "";
  };
};
